import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../context/OrganizationContext';
import { useRBAC } from '../context/RBACContext';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  InputBase,
  IconButton,
  Snackbar,
  Alert,
  Pagination,
  Select,
  MenuItem,
  CircularProgress,
  Backdrop,
  Button,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { ledeSx } from '../theme/styleHelpers';

// Import our content components
import UniversalSearchBar, { SearchQuery } from '../components/content/UniversalSearchBar';
import { activeFilterCount } from '../utils/activeFilterCount';
import ViewModeToggle, { StandardViewMode } from '../components/content/ViewModeToggle';
import BannerStyleToggle, { BannerStyle } from '../components/content/BannerStyleToggle';
import FilterSidebar, { ActiveFilters } from '../components/content/FilterSidebar';
import ContentCard from '../components/content/ContentCard';
import ShareDialog from '../components/shared/ShareDialog';
import { ContentMetadataDialog } from '../components/content/ContentMetadataDialog';
import DeleteContentDialog from '../components/content/DeleteContentDialog';

// Import services
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService, uploadDirectService } from '../services';
import type { Content } from '../types/content.types';
import type { View } from '../types/view.types';

// Import persistent state hooks
import { useLocalStorage } from '../hooks/usePersistentState';

type SortByOption = 'default' | 'createdAt' | 'updatedAt' | 'avgRating' | 'downloadCount' | 'viewCount' | 'title';

const Content: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganizationId, currentTeamId, organizations, teams } = useOrganization();
  const { aggregatedPermissions } = useRBAC();
  const { hasPermission, permissions, debugPermissions } = usePermissions();

  // Get current organization and team context
  const currentOrg = organizations.find(org => org.id === currentOrganizationId);
  const currentTeam = teams.find(team => team.id === currentTeamId);

  // Check if user has permission to create content
  const canCreateContent = hasPermission(PERMISSIONS.CONTENT_CREATE);

  // Debug permissions on mount and when they change
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔒 Content Page - Permission Check:', {
        canCreateContent,
        allPermissions: permissions,
        hasContentCreate: permissions.includes('content:create'),
        permissionCount: permissions.length,
        organizationId: currentOrganizationId,
        teamId: currentTeamId
      });

      if (permissions.length === 0) {
        console.warn('⚠️ No permissions loaded yet. Button will appear once permissions load.');
      }

      debugPermissions();
    }
  }, [permissions, canCreateContent, currentOrganizationId, currentTeamId, debugPermissions]);
  
  // Redirect to dashboard if no team is selected
  useEffect(() => {
    if (!currentTeamId) {
      navigate('/dashboard');
    }
  }, [currentTeamId, navigate]);
  
  // Persistent state management (survives browser sessions via localStorage)
  // These settings are preserved even when closing and reopening the browser
  const [standardViewMode, setStandardViewMode] = useLocalStorage<StandardViewMode>('content-view-mode', 'grid');
  const [bannerStyle, setBannerStyle] = useLocalStorage<BannerStyle>('content-banner-mode', 'illustrations');
  const [sortBy, setSortBy] = useLocalStorage<SortByOption>('content-sort-by', 'default');
  const [filterSidebarCollapsed, setFilterSidebarCollapsed] = useLocalStorage('content-filter-collapsed', false);

  // Workspace-aware session state: reads from sessionStorage but only if the stored
  // workspace key matches the current one. On remount after a workspace navigation the
  // stored key will differ, so defaults are used immediately — no stale-state flash.
  const _currentWorkspaceKey = `${currentOrganizationId ?? ''}|${currentTeamId ?? ''}`;
  const _storedWorkspaceKey = (() => { try { return sessionStorage.getItem('content-workspace-key') ?? ''; } catch { return ''; } })();
  const _workspaceMatch = _storedWorkspaceKey !== '' && _storedWorkspaceKey === _currentWorkspaceKey;

  const _defaultSearchQuery: SearchQuery = { text: '', filters: { funnelStages: [], personas: [], tags: [] } };
  const _defaultActiveFilters: ActiveFilters = {
    mimeTypes: [], tagGroupFilters: {}, systemTags: [], personalTags: [],
    funnelStages: [], personas: [], tags: [], authors: [], organizations: [],
    teams: [], dateRange: { start: null, end: null }, rating: { min: 1, max: 5 },
    bookmarkedOnly: false, statuses: [], personalTagsOnly: false, systemTagsOnly: false,
  };

  const [searchQuery, setSearchQuery] = useState<SearchQuery>(() => {
    if (!_workspaceMatch) return _defaultSearchQuery;
    try { const v = sessionStorage.getItem('content-search-query'); return v ? JSON.parse(v) : _defaultSearchQuery; } catch { return _defaultSearchQuery; }
  });
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(() => {
    if (!_workspaceMatch) return _defaultActiveFilters;
    try { const v = sessionStorage.getItem('content-active-filters'); return v ? JSON.parse(v) : _defaultActiveFilters; } catch { return _defaultActiveFilters; }
  });
  const [filtersOpen, setFiltersOpen] = useState<boolean>(() => activeFilterCount(activeFilters) > 0);
  const [page, setPage] = useState<number>(() => {
    if (!_workspaceMatch) return 1;
    try { const v = sessionStorage.getItem('content-page'); return v ? JSON.parse(v) : 1; } catch { return 1; }
  });

  // Non-persistent state (resets on page load)
  const [content, setContent] = useState<Content[]>([]);
  const [totalContent, setTotalContent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(searchQuery.text);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevWorkspaceRef = useRef<string>(_currentWorkspaceKey);
  const [bookmarkedItems, setBookmarkedItems] = useState<Set<string>>(new Set());
  const [searchFacets, setSearchFacets] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareItem, setShareItem] = useState<{id: string, type: 'content'|'view', title: string} | null>(null);

  // Edit and Delete dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  // Load bookmark status for displayed content
  useEffect(() => {
    const loadBookmarksForContent = async () => {
      if (!currentOrganizationId || content.length === 0) {
        console.log('No org or content, skipping bookmark check');
        setBookmarkedItems(new Set());
        return;
      }

      // Check if user is authenticated before calling bookmark API
      const isAuthenticated = await authService.isAuthenticated();
      if (!isAuthenticated) {
        console.log('User not authenticated, skipping bookmark check');
        setBookmarkedItems(new Set());
        return;
      }

      try {
        // Extract content IDs from current page
        const contentIds = content.map(item => item.id);
        console.log(`Checking bookmark status for ${contentIds.length} items`);

        // Batch check bookmark status
        const result = await contentService.batchCheckBookmarks(contentIds);

        // Create set of bookmarked content IDs
        const bookmarkedContentIds = new Set<string>(
          Object.entries(result.bookmarked)
            .filter(([_, isBookmarked]) => isBookmarked)
            .map(([contentId, _]) => contentId)
        );

        setBookmarkedItems(bookmarkedContentIds);
        console.log(`Found ${bookmarkedContentIds.size} bookmarked items on this page`);
      } catch (error) {
        console.error('Error checking bookmark status:', error);
        setBookmarkedItems(new Set());
      }
    };

    loadBookmarksForContent();
  }, [content, currentOrganizationId]); // Re-check when content changes

  const itemsPerPage = 20;

  // Load content based on current filters and search
  const loadContent = useCallback(async () => {
    if (!currentOrganizationId || !currentTeamId) {
      console.warn('No organization or team selected, skipping content load');
      return;
    }

    setLoading(true);
    try {
      // Build search params based on active filters
      const searchParams = {
        organization_id: currentOrganizationId,
        team_id: currentTeamId || undefined,
        query: searchQuery.text || undefined,
        mime_types: activeFilters.mimeTypes,
        tag_group_filters: activeFilters.tagGroupFilters,
        system_tags: activeFilters.systemTags,
        personal_tags: [
          ...searchQuery.filters.tags,
          ...activeFilters.tags,
          ...activeFilters.personalTags,
        ],
        authors: activeFilters.authors,
        status: activeFilters.statuses,
        min_rating: activeFilters.rating.min !== 1 ? activeFilters.rating.min : undefined,
        max_rating: activeFilters.rating.max !== 5 ? activeFilters.rating.max : undefined,
        created_after: activeFilters.dateRange.start?.toISOString(),
        created_before: activeFilters.dateRange.end?.toISOString(),
        sort_by: sortBy === 'default' ? undefined : sortBy,
        sort_order: sortBy === 'title' ? 'asc' as const : 'desc' as const,
        page: page,
        limit: itemsPerPage,
        fuzzy_search: true,
        include_archived: activeFilters.statuses?.includes('archive') || false,
        bookmarked_only: activeFilters.bookmarkedOnly || undefined,
        include_facets: true,
        // Pass user permissions for RBAC-based content filtering
        user_permissions: aggregatedPermissions,
      };

      // Remove empty arrays and undefined values to clean up the request
      Object.keys(searchParams).forEach(key => {
        const value = (searchParams as any)[key];
        if (value === undefined || (Array.isArray(value) && value.length === 0)) {
          delete (searchParams as any)[key];
        }
      });

      console.log('Loading content with params:', searchParams);
      
      // Use the proper search service for advanced search capabilities
      const result = await searchService.searchContent(searchParams);
      
      console.log('Search API result:', result);

      // Handle the search response - searchContent returns data array and metadata.pagination
      const contentData = result.data || [];

      setContent(contentData);

      // Use pagination metadata from the search result
      const totalCount = result.metadata?.pagination?.totalCount || 0;
      setTotalContent(totalCount);

      // Extract and store search facets for filter sidebar
      if (result.metadata?.facets) {
        console.log('Received search facets:', result.metadata.facets);
        setSearchFacets(result.metadata.facets);
      }
      
      // Show success message if content was loaded
      if (contentData && contentData.length > 0) {
        console.log(`Loaded ${contentData.length} content items`);
      } else {
        console.log('No content found for current filters');
      }
      
    } catch (error) {
      console.error('Error loading content:', error);
      
      // For Phase 2.2 - Show specific message about search API integration
      const errorMessage = error instanceof Error && error.message.includes('search') 
        ? 'Content search is being integrated. Backend search service needs debugging.'
        : 'Error loading content. Please try again.';
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'warning', // Changed to warning to indicate development status
      });
      
      // Set empty state on error
      setContent([]);
      setTotalContent(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, activeFilters, sortBy, page, currentOrganizationId, currentTeamId]);

  // Load content when dependencies change
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Persist search/filter/page to sessionStorage so workspace-aware initializers can read them on remount
  useEffect(() => { try { sessionStorage.setItem('content-search-query', JSON.stringify(searchQuery)); } catch {} }, [searchQuery]);
  useEffect(() => { try { sessionStorage.setItem('content-active-filters', JSON.stringify(activeFilters)); } catch {} }, [activeFilters]);
  useEffect(() => { try { sessionStorage.setItem('content-page', JSON.stringify(page)); } catch {} }, [page]);

  // Keep the stored workspace key current, and clear search state on stay-mounted workspace changes
  // (remount case is handled by the workspace-aware useState initializers above)
  useEffect(() => {
    const key = `${currentOrganizationId ?? ''}|${currentTeamId ?? ''}`;
    if (prevWorkspaceRef.current !== key) {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      setSearchText('');
      setSearchQuery({ text: '', filters: { funnelStages: [], personas: [], tags: [] } });
      setActiveFilters({
        mimeTypes: [], tagGroupFilters: {}, systemTags: [], personalTags: [],
        funnelStages: [], personas: [], tags: [], authors: [], organizations: [],
        teams: [], dateRange: { start: null, end: null }, rating: { min: 1, max: 5 },
        bookmarkedOnly: false, statuses: [], personalTagsOnly: false, systemTagsOnly: false,
      });
      setPage(1);
      prevWorkspaceRef.current = key;
    }
    if (currentOrganizationId || currentTeamId) {
      try { sessionStorage.setItem('content-workspace-key', key); } catch {}
    }
  }, [currentTeamId, currentOrganizationId]);

  // Reset page if current page exceeds total pages (e.g., after search with fewer results)
  useEffect(() => {
    const totalPages = Math.ceil(totalContent / itemsPerPage);
    if (page > totalPages && totalPages > 0) {
      console.log(`Current page ${page} exceeds total pages ${totalPages}, resetting to page 1`);
      setPage(1);
    }
  }, [page, totalContent, itemsPerPage]);

  // Note: We removed the automatic page reset when search/filters change
  // because it was interfering with history state restoration (back button).
  // Instead, we rely on the search/filter handlers to reset the page when needed.

  // Handle search
  const handleSearch = (query: SearchQuery) => {
    setSearchQuery(query);
    setPage(1); // Reset to page 1 when user actively searches
  };

  // Handle filter changes
  const handleFilterChange = (filters: ActiveFilters) => {
    setActiveFilters(filters);
    setPage(1); // Reset to page 1 when user actively changes filters
  };

  // Handle content actions
  const handleContentAction = async (action: string, contentItem: Content) => {
    switch (action) {
      case 'view':
        // In a real app, this would navigate to content detail page
        setSnackbar({
          open: true,
          message: `Viewing: ${contentItem.title}`,
          severity: 'info',
        });
        break;
      
      case 'bookmark':
        // Check authentication before bookmark operations
        const isAuth = await authService.isAuthenticated();
        if (!isAuth) {
          setSnackbar({
            open: true,
            message: 'Please log in to bookmark content',
            severity: 'warning',
          });
          break;
        }

        const isCurrentlyBookmarked = bookmarkedItems.has(contentItem.id);
        try {
          if (isCurrentlyBookmarked) {
            // Find the bookmark to get its ID for deletion
            const bookmarkStatus = await contentService.isContentBookmarked(contentItem.id);
            if (bookmarkStatus.is_bookmarked && bookmarkStatus.bookmark_id) {
              await contentService.deleteBookmark(bookmarkStatus.bookmark_id);
              const newBookmarks = new Set(bookmarkedItems);
              newBookmarks.delete(contentItem.id);
              setBookmarkedItems(newBookmarks);
              setSnackbar({
                open: true,
                message: 'Bookmark removed',
                severity: 'info',
              });
            }
          } else {
            // Create new bookmark
            if (currentOrganizationId) {
              await contentService.createBookmark({
                content_id: contentItem.id
              });
              const newBookmarks = new Set(bookmarkedItems);
              newBookmarks.add(contentItem.id);
              setBookmarkedItems(newBookmarks);
              setSnackbar({
                open: true,
                message: 'Bookmarked successfully',
                severity: 'success',
              });
            } else {
              setSnackbar({
                open: true,
                message: 'Organization context required for bookmarking',
                severity: 'error',
              });
            }
          }
        } catch (error: any) {
          console.error('Error updating bookmark:', error);
          // Show more specific error message if authentication failed
          const errorMessage = error?.response?.status === 401
            ? 'Authentication required. Please log in again.'
            : 'Failed to update bookmark';
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
        }
        break;
      
      case 'download':
        // Implement download using blob method from ContentDetailPage
        const handleDownload = async () => {
          try {
            console.log('Starting direct download for:', contentItem.title);

            // Use blob download method to force actual download behavior
            const { blob, fileName } = await uploadDirectService.downloadFileAsBlob(contentItem.id, contentItem.teamId || '');

            // Create object URL from blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element and trigger direct download
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || contentItem.title;

            // Trigger the download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the object URL
            window.URL.revokeObjectURL(url);

            console.log('Direct download initiated successfully');
            setSnackbar({
              open: true,
              message: `Downloading: ${contentItem.title}`,
              severity: 'success',
            });
          } catch (error) {
            console.error('Direct download failed:', error);
            setSnackbar({
              open: true,
              message: 'Failed to download file. Please try again.',
              severity: 'error',
            });
          }
        };
        handleDownload();
        break;

      case 'share':
        // Check if status is public (handle both string and numeric status)
        // Note: UI should prevent this action for non-public content, but we keep this check as a safety measure
        const statusMap: { [key: number]: string } = {
          0: 'draft',
          1: 'draft',
          2: 'private',
          3: 'public',
          4: 'archived'
        };
        const status = typeof contentItem.status === 'number'
          ? statusMap[contentItem.status]
          : contentItem.status;
        const isPublic = status === 'public';

        if (!isPublic) {
          // This should not happen due to UI-level prevention, but kept as safety check
          console.warn('Share action triggered for non-public content:', {
            contentId: contentItem.id,
            status: contentItem.status,
          });
          setSnackbar({
            open: true,
            message: 'Only public content can be shared via link',
            severity: 'error',
          });
          break;
        }

        setShareItem({
          id: contentItem.id,
          // Derive from kind/mime — the unified content list shows both
          // files (kind='content'/'file') and views (kind='view'). The public-link
          // record must carry the correct discriminator so the controller hits
          // the right collection and PublicLinkAccess renders the right flow.
          type: ((contentItem as any).kind === 'view'
            || (contentItem as any).mimeType === 'application/vnd.sharp.view')
            ? 'view'
            : 'content',
          title: contentItem.title
        });
        setShareDialogOpen(true);
        break;
      
      case 'edit':
        // Open edit dialog — ContentMetadataDialog loads its own tags internally
        setSelectedContent(contentItem);
        setEditDialogOpen(true);
        break;

      case 'delete':
        // Open delete confirmation dialog
        setSelectedContent(contentItem);
        setDeleteDialogOpen(true);
        break;

      default:
        setSnackbar({
          open: true,
          message: `Action: ${action} on ${contentItem.title}`,
          severity: 'info',
        });
    }
  };

  // Handle upload action
  const handleUpload = () => {
    navigate('/content/upload');
  };

  // Handle saving content edits
  const handleSaveContentEdits = async (updates: {
    title: string;
    description: string;
    status?: string;
    tag_ids: string[];
  }) => {
    if (!selectedContent) return;

    try {
      console.log('Saving content updates:', updates);

      const updateData: any = {
        title: updates.title,
        description: updates.description,
        tags: updates.tag_ids,
        team_id: selectedContent.teamId || currentTeamId,
      };

      if (updates.status) {
        updateData.status = updates.status;
      }

      await contentService.updateContent(selectedContent.id, updateData);

      console.log('Content updated successfully');

      setSnackbar({
        open: true,
        message: 'Content updated successfully',
        severity: 'success',
      });

      // Reload content list to show updated data.
      // Do NOT close the dialog here — the dialog's handleSave calls onClose() explicitly
      // after onSave returns, and handleRegenerateEnrichment keeps the dialog open.
      await loadContent();
    } catch (error: any) {
      console.error('Error updating content:', error);
      setSnackbar({
        open: true,
        message: `Failed to update content: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        severity: 'error',
      });
      throw error;
    }
  };

  // Handle deleting content
  const handleDeleteContent = async () => {
    if (!selectedContent) return;

    const teamId = selectedContent.teamId || currentTeamId;
    if (!teamId) {
      setSnackbar({
        open: true,
        message: 'Team ID is required to delete content',
        severity: 'error',
      });
      return;
    }

    try {
      console.log('Deleting content:', selectedContent.id);

      await contentService.deleteContent(selectedContent.id, teamId);

      console.log('Content deleted successfully');

      setSnackbar({
        open: true,
        message: 'Content deleted successfully',
        severity: 'success',
      });

      // Reload content list to show updated data
      await loadContent();

      setDeleteDialogOpen(false);
      setSelectedContent(null);
    } catch (error: any) {
      console.error('Error deleting content:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete content: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        severity: 'error',
      });
      throw error;
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const totalPages = Math.ceil(totalContent / itemsPerPage);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        {/* Page h1 */}
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: '-0.025em',
            lineHeight: 1.15,
            marginBottom: 1,
          }}
        >
          Content Library
        </Typography>

        {/* Lede paragraph */}
        <Typography sx={ledeSx}>
          Browse, search, and manage your team&apos;s content.{' '}
          {totalContent} {totalContent === 1 ? 'result' : 'results'} matching current filters.
        </Typography>
      </Box>

      {/* Toolbar row: centered [toggle + search input], upload button anchored right */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 2.5, flexWrap: 'wrap' }}>
        {/* Left spacer — mirrors the right section so the toggle + search pair is centered */}
        <Box sx={{ flex: 1, minWidth: 0 }} aria-hidden />

        {/* Filters toggle — icon-only button with badge showing active filter count */}
        <Tooltip title="Filters">
          <IconButton
            aria-label="Filters"
            aria-pressed={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}
            sx={(theme) => ({
              width: 46,
              height: 46,
              borderRadius: '14px',
              border: `1px solid ${theme.palette.divider}`,
              background: filtersOpen ? theme.palette.brand.teal : theme.palette.background.paper,
              color: filtersOpen ? '#fff' : theme.palette.text.secondary,
              '&:hover': {
                background: filtersOpen ? theme.palette.brand.teal : theme.palette.background.paper,
                color: filtersOpen ? '#fff' : theme.palette.text.primary,
              },
            })}
          >
            <Badge
              badgeContent={activeFilterCount(activeFilters)}
              color="primary"
              max={99}
            >
              <FilterListIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Inline search input — always shown now that views tab is removed */}
        <Box sx={(theme) => ({
            width: 576, maxWidth: '100%',
            display: 'flex', alignItems: 'center', gap: '10px',
            paddingLeft: '14px', paddingRight: '14px', borderRadius: '999px', height: 46,
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          })}>
            <SearchIcon sx={(theme) => ({ fontSize: 17, color: theme.palette.text.disabled })} />
            <InputBase
              placeholder="Search content, tags, descriptions…"
              value={searchText}
              onChange={(e) => {
                const val = e.target.value;
                setSearchText(val);
                if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                if (val === '') {
                  handleSearch({ ...searchQuery, text: '' });
                } else {
                  searchDebounceRef.current = setTimeout(() => {
                    handleSearch({ ...searchQuery, text: val });
                  }, 3000);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                  handleSearch({ ...searchQuery, text: searchText });
                }
              }}
              sx={{ flex: 1, fontSize: 16 }}
            />
            {searchText && (
              <IconButton
                size="small"
                onClick={() => {
                  setSearchText('');
                  if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                  handleSearch({ ...searchQuery, text: '' });
                }}
                sx={{ p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>

        {/* Right section — wrapper with flex: 1 so it mirrors the left spacer
            and pushes the upload button to the right while the centered group stays centered */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
          {/* Upload button — only show if user has content:create permission */}
          {canCreateContent && (
            <Button
              variant="contained"
              size="small"
              onClick={handleUpload}
              startIcon={<UploadIcon />}
            >
              Upload Content
            </Button>
          )}
        </Box>
      </Box>

      {/* Main Content Area — unified content + view search */}
      <Grid container spacing={3}>
            {/* Filter Sidebar */}
            {filtersOpen && (
              <Grid item xs={12} md={3}>
                <FilterSidebar
                  activeFilters={activeFilters}
                  onChange={handleFilterChange}
                  collapsed={filterSidebarCollapsed}
                  onToggleCollapse={() => setFilterSidebarCollapsed(!filterSidebarCollapsed)}
                  searchFacets={searchFacets}
                />
              </Grid>
            )}

            {/* Results Area */}
            <Grid item xs={12} md={filtersOpen ? 9 : 12}>
          {/* Results row — count pill + view toggle + sort */}
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1.75, fontSize: 12.5 }}>
            {/* Count pill */}
            <Box sx={(theme) => ({
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              padding: '4px 12px', borderRadius: '999px',
              background: theme.palette.mode === 'light' ? 'rgba(34,181,220,.10)' : 'rgba(34,181,220,.16)',
              color: theme.palette.mode === 'light' ? theme.palette.brand.teal : theme.palette.brand.cyan,
              fontSize: 12.5,
            })}>
              {loading ? (
                <Box component="span" sx={{ fontSize: 12.5 }}>Loading…</Box>
              ) : (
                <>
                  <Box component="span" sx={(theme) => ({
                    fontWeight: 800, fontSize: 13.5,
                    color: theme.palette.mode === 'light' ? theme.palette.brand.navy : '#fff',
                  })}>{totalContent}</Box>{' '}
                  {totalContent === 1 ? 'result found' : 'results found'}
                </>
              )}
            </Box>

            {/* View toggle + sort — right side */}
            <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <ViewModeToggle
                standardModes={['list', 'grid']}
                activeStandardMode={standardViewMode}
                customViews={[]}
                activeCustomView={null}
                onStandardModeChange={setStandardViewMode}
                onCustomViewChange={() => {}}
                onManageViews={() => navigate('/views')}
                showLabels
              />

              <BannerStyleToggle value={bannerStyle} onChange={setBannerStyle} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Typography sx={(theme) => ({
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: theme.palette.text.secondary,
                })}>
                  Sort
                </Typography>
                <Select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value as SortByOption);
                    setPage(1);
                  }}
                  size="small"
                  IconComponent={(props) => <ExpandMoreIcon {...props} sx={{ fontSize: 15 }} />}
                  sx={(theme) => ({
                    fontSize: 12.5,
                    fontWeight: 600,
                    background: theme.palette.background.paper,
                    borderRadius: '8px',
                    height: 30,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSelect-select': { padding: '5px 10px', paddingRight: '24px' },
                  })}
                >
                  <MenuItem value="default">Best Match</MenuItem>
                  <MenuItem value="createdAt">Newest First</MenuItem>
                  <MenuItem value="avgRating">Highest Rated</MenuItem>
                  <MenuItem value="downloadCount">Most Downloaded</MenuItem>
                  <MenuItem value="viewCount">Most Viewed</MenuItem>
                  <MenuItem value="title">Title A-Z</MenuItem>
                </Select>
              </Box>
            </Box>
          </Box>

          {/* Loading Backdrop */}
          <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <CircularProgress color="inherit" />
          </Backdrop>

          {/* Content Grid/List */}
          {!loading && content.length > 0 && (
            <>
              {(() => {
                const displayContent = bannerStyle === 'illustrations'
                  ? content.map((item) => ({ ...item, thumbnailUrl: '', thumbnail_url: '' }))
                  : content;
                return standardViewMode === 'list' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {displayContent.map((item) => (
                      <ContentCard
                        key={item.id}
                        content={item}
                        viewMode={standardViewMode}
                        onAction={handleContentAction}
                        bookmarked={bookmarkedItems.has(item.id)}
                      />
                    ))}
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                      {displayContent.map((item) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={item.id}>
                          <ContentCard
                            content={item}
                            viewMode={standardViewMode}
                            onAction={handleContentAction}
                            bookmarked={bookmarkedItems.has(item.id)}
                          />
                        </Grid>
                      ))}
                  </Grid>
                );
              })()}

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(event, newPage) => setPage(newPage)}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}

          {/* No Results */}
          {!loading && content.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                No content found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search terms or filters to find what you're looking for.
              </Typography>
            </Paper>
          )}
        </Grid>
        </Grid>

      {/* Debug info - Only in development (shown when permissions are still loading) */}
      {process.env.NODE_ENV === 'development' && !canCreateContent && permissions.length === 0 && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            p: 2,
            backgroundColor: 'warning.light',
            maxWidth: 300,
          }}
        >
          <Typography variant="caption" color="warning.dark">
            Debug: Permissions loading... If this persists, check console logs.
          </Typography>
        </Paper>
      )}


      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Share Dialog */}
      {shareItem && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setShareItem(null);
          }}
          itemId={shareItem.id}
          itemType={shareItem.type}
          itemTitle={shareItem.title}
        />
      )}

      {/* Edit Content Dialog */}
      {selectedContent && (
        <ContentMetadataDialog
          open={editDialogOpen}
          initialValues={{
            title: selectedContent.title,
            description: selectedContent.description,
            status: selectedContent.status ?? 'draft',
            tagIds: selectedContent.tag_ids ?? selectedContent.tags ?? [],
            thumbnailUrl: selectedContent.thumbnailUrl,
            teamId: selectedContent.teamId,
            organizationId: selectedContent.organizationId,
            aiDescription: selectedContent.aiDescription,
            aiSuggestedTitle: selectedContent.aiSuggestedTitle,
            suggestedTags: selectedContent.suggestedTags,
          }}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedContent(null);
          }}
          onSave={async (values) => {
            await handleSaveContentEdits({
              title: values.title,
              description: values.description,
              status: values.status,
              tag_ids: values.tagIds,
            });
          }}
          kind={(selectedContent.kind as 'file' | 'view') ?? 'file'}
          contentId={selectedContent.id || undefined}
          onEnrichmentTriggered={(contentId) => {
            // Keep the dialog open — it stays mounted and polls internally
            // Optimistically mark the item as processing so the chip appears immediately
            setContent(prev => prev.map(item =>
              item.id === contentId
                ? { ...item, processingState: 'ai_enrichment_pending', isFullySearchable: false }
                : item
            ));
          }}
          onEnrichmentComplete={(contentId) => {
            // Clear the processing chip — enrichment finished.
            // Do NOT update selectedContent here — it would change the content prop,
            // trigger the init useEffect, and wipe the aiSuggestions the dialog just set.
            setContent(prev => prev.map(item =>
              item.id === contentId
                ? { ...item, processingState: 'search_indexed', isFullySearchable: true }
                : item
            ));
          }}
          availableStatuses={['draft', 'private', 'public', 'archived']}
        />
      )}

      {/* Delete Content Dialog */}
      {selectedContent && (
        <DeleteContentDialog
          open={deleteDialogOpen}
          content={selectedContent}
          onClose={() => {
            setDeleteDialogOpen(false);
            setSelectedContent(null);
          }}
          onConfirm={handleDeleteContent}
        />
      )}
    </Container>
  );
};

export default Content;